'use client'

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { api } from '@/lib/api'
import { getTenantCacheKey } from '@/lib/tenantCache'

interface Branch {
  tenant_id: string
  name: string
  label?: string
  address?: string
  logo_url?: string
  domain: string
  is_current: boolean
}

interface BranchesContextType {
  groupName: string | null
  groupLogo: string | null
  branches: Branch[]
  hasBranches: boolean
  loading: boolean
}

const BranchesContext = createContext<BranchesContextType | undefined>(undefined)

const BRANCHES_CACHE_KEY = 'branches_info_cache'
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutos

export function BranchesProvider({ children, skipFetch = false }: { children: ReactNode; skipFetch?: boolean }) {
  const [groupName, setGroupName] = useState<string | null>(null)
  const [groupLogo, setGroupLogo] = useState<string | null>(null)
  const [branches, setBranches] = useState<Branch[]>([])
  const [loading, setLoading] = useState(true)

  const loadBranches = async () => {
    try {
      // Check cache first
      if (typeof window !== 'undefined') {
        const cached = localStorage.getItem(getTenantCacheKey(BRANCHES_CACHE_KEY))
        if (cached) {
          try {
            const { data, timestamp } = JSON.parse(cached)
            const now = Date.now()

            if (now - timestamp < CACHE_DURATION) {
              setGroupName(data.group_name)
              setGroupLogo(data.group_logo)
              setBranches(data.branches || [])
              setLoading(false)
              // Background refresh solo si no estamos en admin
              if (!skipFetch) {
                api.get('/public/branches').then(freshData => {
                  setGroupName(freshData.group_name)
                  setGroupLogo(freshData.group_logo)
                  setBranches(freshData.branches || [])
                  localStorage.setItem(getTenantCacheKey(BRANCHES_CACHE_KEY), JSON.stringify({
                    data: freshData,
                    timestamp: Date.now()
                  }))
                }).catch(() => {})
              }
              return
            }
          } catch (e) {
            localStorage.removeItem(getTenantCacheKey(BRANCHES_CACHE_KEY))
          }
        }
      }

      // If skipFetch and we have no cache, don't fetch
      if (skipFetch) {
        setLoading(false)
        return
      }

      const data = await api.get('/public/branches')
      setGroupName(data.group_name)
      setGroupLogo(data.group_logo)
      setBranches(data.branches || [])

      if (typeof window !== 'undefined') {
        localStorage.setItem(getTenantCacheKey(BRANCHES_CACHE_KEY), JSON.stringify({
          data,
          timestamp: Date.now()
        }))
      }
    } catch (error) {
      console.error('Error loading branches:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadBranches()
  }, [])

  const hasBranches = branches.length > 1

  return (
    <BranchesContext.Provider value={{ groupName, groupLogo, branches, hasBranches, loading }}>
      {children}
    </BranchesContext.Provider>
  )
}

export function useBranches() {
  const context = useContext(BranchesContext)
  if (context === undefined) {
    throw new Error('useBranches must be used within a BranchesProvider')
  }
  return context
}
